import React from 'react';
import { Line, Bar } from 'react-chartjs-2';

const BAR_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: 'rgba(15, 23, 42, 0.9)',
      titleFont: { size: 11, weight: 'bold' },
      bodyFont: { size: 11 },
      padding: 10,
      cornerRadius: 6,
      displayColors: false,
      callbacks: {
        label: (ctx) => ` ${ctx.parsed.y} leads`,
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: { color: 'rgba(0,0,0,0.04)', borderDash: [4, 4] },
      ticks: {
        font: { size: 10, family: "'DM Sans', sans-serif" },
        color: '#94a3b8',
        stepSize: 1,
        precision: 0,
        callback: (value) => value,
      },
      border: { display: false },
    },
    x: {
      grid: { color: 'rgba(0,0,0,0.04)', borderDash: [4, 4], display: false },
      ticks: { font: { size: 10, family: "'DM Sans', sans-serif" }, color: '#64748b' },
      border: { display: false },
    },
  },
  borderRadius: 8,
  barPercentage: 0.6,
};

const PROJECT_BAR_OPTS = {
  ...BAR_OPTS,
  plugins: {
    ...BAR_OPTS.plugins,
    tooltip: {
      ...BAR_OPTS.plugins.tooltip,
      callbacks: {
        label: (ctx) => ` ${ctx.parsed.y} projects`,
      },
    },
  },
};

export const PipelineChartsSection = ({ mockLeads, mockProjects, monthlySalesBarData }) => {
  // Build project status counts
  const projectStatusGroups = {
    'Not Started': { label: 'Not Started' },
    'Materials': { label: 'Materials' },
    'Started': { label: 'Started' },
    'Installation': { label: 'Installation' },
    'Docs': { label: 'Docs' },
    'Accounts': { label: 'Accounts' },
    'Completed': { label: 'Completed' },
  };

  const projectCounts = {
    'Not Started': 0,
    'Materials': 0,
    'Started': 0,
    'Installation': 0,
    'Docs': 0,
    'Accounts': 0,
    'Completed': 0,
  };

  (mockProjects || []).forEach((p) => {
    const st = (p.projectStatus || p.status || '').toLowerCase().trim();
    
    if (st === 'not started') {
      projectCounts['Not Started']++;
    } else if (
      st === 'team assignment' || 
      st === 'material requested' || 
      st === 'pending store' || 
      st === 'owner approval' || 
      st === 'dispatched'
    ) {
      projectCounts['Materials']++;
    } else if (st === 'project started') {
      projectCounts['Started']++;
    } else if (
      st === 'carpenter complete' || 
      st === 'electrician complete' || 
      st === 'painter complete' || 
      st === 'plumber complete'
    ) {
      projectCounts['Installation']++;
    } else if (st === 'documents') {
      projectCounts['Docs']++;
    } else if (st === 'bill approval') {
      projectCounts['Accounts']++;
    } else if (st === 'completed') {
      projectCounts['Completed']++;
    }
  });

  const projectLabels = Object.keys(projectStatusGroups).map((k) => projectStatusGroups[k].label);
  const projectData = Object.keys(projectStatusGroups).map((k) => projectCounts[k] || 0);

  const projectsChartData = {
    labels: projectLabels,
    datasets: [
      {
        label: 'Projects',
        data: projectData,
        backgroundColor: '#7ba2bf',
        borderRadius: 4,
        barPercentage: 0.6,
      },
    ],
  };

  // Adjust tooltip and scales for sales chart
  const salesBarOpts = {
    ...BAR_OPTS,
    plugins: {
      ...BAR_OPTS.plugins,
      tooltip: {
        ...BAR_OPTS.plugins.tooltip,
        callbacks: {
          label: (ctx) => ` ₹${ctx.parsed.y.toLocaleString('en-IN')}`,
        },
      },
    },
    scales: {
      ...BAR_OPTS.scales,
      y: {
        ...BAR_OPTS.scales.y,
        ticks: {
          ...BAR_OPTS.scales.y.ticks,
          stepSize: undefined, // Remove fixed step size
          callback: (value) => {
            if (value >= 1000) return '₹' + (value / 1000).toFixed(1) + 'k';
            return '₹' + value;
          }
        }
      }
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Monthly Sales Distribution */}
      <div className="card p-5 border border-gray-100 rounded-xl shadow-sm bg-white">
        <div className="mb-5">
          <h3 className="text-lg font-semibold text-slate-800 font-sans tracking-tight">Monthly Sales</h3>
          <p className="text-xs text-slate-400 mt-0.5">Sales revenue distributed by month</p>
        </div>
        <div style={{ height: 220 }}>
          {monthlySalesBarData && <Bar data={monthlySalesBarData} options={salesBarOpts} />}
        </div>
      </div>

      {/* Project Status Distribution */}
      <div className="card p-5">
        <div className="mb-5">
          <h3 className="text-lg font-semibold text-slate-800 font-sans tracking-tight">Monthly Project Distribution</h3>
          <p className="text-xs text-slate-400 mt-0.5">Projects by current stage</p>
        </div>
        <div style={{ height: 220 }}>
          <Bar data={projectsChartData} options={PROJECT_BAR_OPTS} />
        </div>
      </div>
    </div>
  );
};





